import { useContext, useState } from "react"
import InputFieldWithLabel from "../../Inputs/InputFieldWithLabel"
import React from "react"
import Button from "../../../ui/Button"

import api from "../../../../api2"
import SelectInput, { Option } from "../../../ui/SelectInput"
import { titleCase } from "../../../../utils"
import IAddOn, { APPLICABLE_AT } from "../../../../interfaces/IAddOn"
import { ModalContext } from "~/contexts/admin/ModalContext"
import Toggle from "../../../ui/Toggle"
import { productTypes } from "./Products"
import InputFieldWithCurrency from "../../Inputs/InputFieldWithCurrency"
import HoverTip from "../../../ui/HoverTip"
import formatCampingStyleName from "../../../../utils/formatCampingStyleName"
import { startCase } from "lodash"
import { useProducts } from "~/api2/hooks/useProducts"
import parseDateTime from "~/utils/parseDateTime"
import { isMobile } from "react-device-detect"
import { useCamp } from "~/api2/hooks/useCamp"
import { useCampingStyles } from "~/api2/hooks/useCampingStyles"
import { useNotification } from "~/contexts/NotificationProvider"
import CouponsDatePicker from "~/components/Admin/Settings/Coupons/CouponsModalContent/CouponsDatePicker"

const ProductModalContent = ({
  selectedProduct,
  fetchProducts,
}: {
  selectedProduct: IAddOn | null
  fetchProducts?: () => Promise<void>
}) => {
  const { refetch: refetchProducts } = useProducts()
  const {
    index: { data: camp },
  } = useCamp()
  const {
    index: { data: campingStyles },
  } = useCampingStyles()
  const { showNotification } = useNotification()
  const { onClose } = useContext(ModalContext)
  const [selectedCampingStyleId, setSelectedCampingStyleId] = useState<string>(
    selectedProduct?.camping_style_id || "all",
  )
  const [label, setLabel] = useState(selectedProduct?.label || "")
  const [selectedType, setSelectedType] = useState(
    selectedProduct ? productTypes.find((style) => style?.value == selectedProduct?.kind)?.value : "normal",
  )
  const [description, setDescription] = useState(selectedProduct?.description || "")
  const [unitPrice, setUnitPrice] = useState(selectedProduct?.amount_in_cents / 100 || null)
  const [inventoryQuantity, setInventoryQuantity] = useState(selectedProduct?.inventory_quantity || null)
  const [sku, setSku] = useState(selectedProduct?.sku || null)
  const [isBookable, setIsBookable] = useState(selectedProduct?.is_bookable || false)
  const [isAppliedNightly, setIsAppliedNightly] = useState(selectedProduct?.is_applied_nightly || false)
  const [isRemoveButtonClicked, setIsRemoveButtonClicked] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const [applicableAt, setApplicableAt] = useState<string>(selectedProduct?.applicable_at || "anytime")
  const [validFrom, setValidFrom] = useState<string>(selectedProduct?.valid_from || new Date().toISOString())
  const [validUntil, setValidUntil] = useState<string>(selectedProduct?.valid_until || new Date().toISOString())

  const handleCreateSave = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsLoading(true)

    api.AddOns.create(camp.id, {
      label,
      slug: label.toLowerCase().replace(/ /g, "-"),
      camp_id: camp.id,
      camping_style_id: selectedCampingStyleId == "all" ? null : selectedCampingStyleId,
      kind: selectedType,
      description,
      amount_in_cents: unitPrice * 100,
      is_applied_nightly: isAppliedNightly,
      inventory_quantity: inventoryQuantity,
      sku: sku,
      is_bookable: isBookable,
      valid_from:
        applicableAt === APPLICABLE_AT.FROM_DATE || applicableAt === APPLICABLE_AT.IN_DATE_RANGE
          ? parseDateTime(validFrom, camp.timezone)
          : null,
      valid_until:
        applicableAt === APPLICABLE_AT.UNTIL_DATE || applicableAt === APPLICABLE_AT.IN_DATE_RANGE
          ? parseDateTime(validUntil, camp.timezone)
          : null,
    })
      .then(() => {
        showNotification({
          type: "success",
          title: "Product Created!",
          description: "The product was successfully created.",
        })
        onClose()
        setIsLoading(false)
        refetchProducts()
        fetchProducts?.()
      })
      .catch((error) => {
        let errorMessage = "An error occurred while saving the product."
        console.log(error)

        if (error?.debug?.message?.includes("UniqueViolation")) {
          errorMessage = "This SKU already exists, please use a different one."
        } else if (error.errors) {
          errorMessage = Object.entries(error.errors)
            .map(([key, values]) => `${startCase(key)} ${values?.join(", ")}`)
            .join(", ")
        }

        showNotification({
          type: "error",
          title: errorMessage,
        })
        setIsLoading(false)
      })
  }

  const handleEditSave = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsLoading(true)

    api.AddOns.update(selectedProduct.id, {
      camp_id: camp.id,
      camping_style_id: selectedCampingStyleId == "all" ? null : selectedCampingStyleId,
      is_bookable: isBookable,
      label,
      slug: label.toLowerCase().replace(/ /g, "-"),
      kind: selectedType,
      description,
      amount_in_cents: unitPrice * 100,
      is_applied_nightly: isAppliedNightly,
      inventory_quantity: inventoryQuantity,
      sku: sku,
      valid_from:
        applicableAt === APPLICABLE_AT.FROM_DATE || applicableAt === APPLICABLE_AT.IN_DATE_RANGE
          ? parseDateTime(validFrom, camp.timezone)
          : null,
      valid_until:
        applicableAt === APPLICABLE_AT.UNTIL_DATE || applicableAt === APPLICABLE_AT.IN_DATE_RANGE
          ? parseDateTime(validUntil, camp.timezone)
          : null,
    })
      .then((product) => {
        showNotification({
          type: "success",
          title: "Changes Saved!",
          description: "The product you modified were successfully updated.",
        })
        setIsLoading(false)
        refetchProducts()
        fetchProducts?.()
      })
      .catch((error) => {
        const formatErrors = (errorObject: { errors: { [key: string]: string[] } }): string => {
          return Object.entries(errorObject.errors)
            .map(([key, values]) => `${startCase(key)} ${values.join(", ")}`)
            .join(", ")
        }

        const formattedErrors = formatErrors(error)

        showNotification({
          type: "error",
          title: formattedErrors,
        })
        setIsLoading(false)
      })
      .finally(onClose)
  }

  const handleCancel = (e: React.FormEvent<HTMLButtonElement>) => {
    e.preventDefault()
    onClose()
  }

  const handleRemoveClick = (e) => {
    isRemoveButtonClicked ? handleArchive(e) : setIsRemoveButtonClicked(true)
  }

  const handleArchive = (e: React.FormEvent<HTMLButtonElement>) => {
    setIsRemoveButtonClicked(false)
    e.preventDefault()
    setIsLoading(true)

    api.AddOns.archive(selectedProduct.id)
      .then(() => {
        setIsLoading(false)
        refetchProducts()
      })
      .finally(onClose)
  }

  const campingStyleOptions = campingStyles?.map((style) => ({
    label: formatCampingStyleName(style.name),
    value: style.id,
  }))

  campingStyleOptions?.unshift({ label: "All site types", value: "all" })

  const applicableAtOptions = [
    { label: "Anytime", value: "anytime" },
    { label: "From a specific date", value: "from_date" },
    { label: "Until specific date", value: "until_date" },
    { label: "During time range", value: "in_date_range" },
  ]

  return (
    <form className="flex flex-col justify-between">
      <div className="flex flex-col gap-y-5">
        <div className="font-semibold text-xl pb-4 mb-1 border-b border-gray-200">
          {selectedProduct ? "Edit" : "Create"} product
        </div>
        <div className="text-baselg font-semibold -mb-2">Basic settings</div>
        <div className="flex items-center gap-x-4">
          <InputFieldWithLabel
            label="Name"
            required
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className=""
          />
          <div className="relative">
            {!!selectedProduct?.addable_camping_style_id?.length ? (
              <HoverTip
                className="my-2"
                content={`This product is attached to the corresponding ${titleCase(campingStyles?.find((style) => style.id === selectedProduct.camping_style_id)?.name)} site's price`}
              >
                <InputFieldWithCurrency
                  label="Unit price"
                  currency={camp.currency}
                  required
                  value={unitPrice}
                  onChange={(e) => setUnitPrice(e.target.value)}
                  disabled={!!selectedProduct?.addable_camping_style_id?.length}
                />
              </HoverTip>
            ) : (
              <InputFieldWithCurrency
                label="Unit price"
                currency={camp.currency}
                required
                value={unitPrice}
                onChange={(e) => setUnitPrice(e.target.value)}
                disabled={!!selectedProduct?.addable_camping_style_id?.length}
                className="w-36"
              />
            )}
          </div>
          <InputFieldWithLabel
            label={isMobile ? "Inventory" : "Inventory quantity"}
            type="number"
            value={inventoryQuantity}
            onChange={(e) => setInventoryQuantity(e.target.value)}
            className="w-36"
            placeholder="Unlimited"
          />
          <InputFieldWithLabel label="SKU" value={sku} onChange={(e) => setSku(e.target.value)} className="w-36" />
        </div>
        <InputFieldWithLabel
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-64 xs:w-auto"
        />
        <div className="text-baselg font-semibold -mb-2 mt-4">Advanced settings</div>
        <Toggle
          checked={isBookable}
          onChange={setIsBookable}
          name="isBookable"
          label="Bookable"
          description="If enabled, this product will show up during online booking"
        />
        <Toggle
          checked={isAppliedNightly}
          onChange={setIsAppliedNightly}
          name="isAppliedNightly"
          label="Applied nightly"
          description="If enabled, this add-on will apply to each night of the reservation"
        />
        <div className="flex items-center justify-between">
          <div>
            <div className="text-gray-900 text-md">Type</div>
            <div className="text-gray-500 text-sm">
              {productTypes.find((style) => style.value === selectedType).description}
            </div>
          </div>
          <div className="w-72 ml-3">
            <SelectInput
              labelClassName="font-medium"
              options={productTypes}
              selected={productTypes.find((style) => style.value === selectedType)}
              setSelected={(option: Option) => setSelectedType(option.value)}
            />
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-gray-900 text-md">
              Applicable for<span className="text-red-600 ml-0.5 relative top-[-0.125rem]">*</span>
            </div>
            <div className="text-gray-500 text-sm">Determines if applicable for some or all site types</div>
          </div>
          <div className="w-72 ml-3">
            {campingStyleOptions && (
              <SelectInput
                labelClassName="font-medium"
                options={campingStyleOptions}
                selected={campingStyleOptions.find((style) => style.value === selectedCampingStyleId)}
                setSelected={(option: Option) => {
                  setSelectedCampingStyleId(option.value)
                }}
              />
            )}
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-gray-900 text-md">
              Applicable at<span className="text-red-600 text-lg ml-0.5">*</span>
            </div>
            <div className="text-gray-500 text-sm">Determines when the product is applicable</div>
          </div>
          <div className="w-72 ml-3">
            <SelectInput
              labelClassName="font-medium"
              options={applicableAtOptions}
              selected={applicableAtOptions.find((option) => option.value === applicableAt)}
              setSelected={(option: Option) => setApplicableAt(option.value)}
            />
          </div>
        </div>
        <div className="flex items-center gap-4 justify-between">
          {[APPLICABLE_AT.FROM_DATE, APPLICABLE_AT.IN_DATE_RANGE].includes(applicableAt) && validFrom && (
            <div className="flex justify-start items-center gap-2 ">
              <div className="text-gray-900 text-md">
                From<span className="text-red-600 text-lg ml-0.5">*</span>
              </div>
              <CouponsDatePicker
                isPickerImageHidden
                selectedDate={validFrom}
                setSelectedDate={setValidFrom}
                isStartDate={"start-date"}
                className="!w-60 h-9"
              />
            </div>
          )}
          {[APPLICABLE_AT.UNTIL_DATE, APPLICABLE_AT.IN_DATE_RANGE].includes(applicableAt) && validUntil && (
            <div className="flex justify-start items-center gap-2 w-72 ml-3">
              <div className="text-gray-900 text-md">
                Until<span className="text-red-600 text-lg ml-0.5">*</span>
              </div>
              <CouponsDatePicker
                isPickerImageHidden
                selectedDate={validUntil}
                setSelectedDate={setValidUntil}
                isStartDate={"end-date"}
                className="!w-full h-9"
              />
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-col xs:flex-row xs:justify-between border-t border-gray-200 pt-5 mt-6">
        <Button
          type="button"
          className={!selectedProduct && "invisible"}
          variant="red"
          children={!isRemoveButtonClicked ? "Delete" : "Are you sure?"}
          onClick={handleRemoveClick}
          disabled={isLoading}
        />

        <div className="flex gap-4 mt-3 xs:mt-0">
          <Button
            variant="white"
            children={"Cancel"}
            className="flex-auto xs:flex-initial w-32"
            onClick={handleCancel}
            disabled={isLoading}
          />
          <Button
            variant="green"
            children={"Save"}
            className="flex-auto xs:flex-initial w-32"
            onClick={selectedProduct ? handleEditSave : handleCreateSave}
            disabled={isLoading}
          />
        </div>
      </div>
    </form>
  )
}

export default ProductModalContent
